/**
 * Which of a template's questions go into a new assessment.
 *
 * The selection is stored as the set of excluded question ids, so a template
 * starts with everything included. Ids come from positions rather than keys
 * because built-in templates reuse question keys across question sets.
 *
 * A group (a category or a question set) is on when all its questions are
 * included, off when none are and mixed otherwise.
 */

export const SELECTION = {
  ON: 'on',
  OFF: 'off',
  MIXED: 'mixed',
};

export function questionId(setIndex, questionIndex) {
  return `${setIndex}:${questionIndex}`;
}

export function questionSetIds(questionSet, setIndex) {
  return (questionSet.questions ?? []).map((_, questionIndex) =>
    questionId(setIndex, questionIndex)
  );
}

export function selectionState(ids, excluded) {
  const excludedCount = ids.filter((id) => excluded.has(id)).length;

  if (excludedCount === 0) {
    return SELECTION.ON;
  }

  return excludedCount === ids.length ? SELECTION.OFF : SELECTION.MIXED;
}

/**
 * Flips a group the way a mixed checkbox does: a group that is off or mixed
 * turns fully on, and a group that is on turns fully off.
 */
export function toggleGroup(ids, excluded) {
  const next = new Set(excluded);
  const turnOn = selectionState(ids, excluded) !== SELECTION.ON;

  ids.forEach((id) => (turnOn ? next.delete(id) : next.add(id)));

  return next;
}

export function countIncluded(questionSets, excluded) {
  return questionSets.reduce(
    (count, questionSet, setIndex) =>
      count +
      questionSetIds(questionSet, setIndex).filter((id) => !excluded.has(id))
        .length,
    0
  );
}

/**
 * The question sets to create the assessment with. A set whose questions are
 * all excluded is dropped, but one that never had questions is kept as is.
 */
export function selectQuestionSets(questionSets, excluded) {
  return questionSets
    .map((questionSet, setIndex) => {
      const questions = questionSet.questions ?? [];

      if (questions.length === 0) {
        return questionSet;
      }

      const kept = questions.filter(
        (_, questionIndex) => !excluded.has(questionId(setIndex, questionIndex))
      );

      return kept.length === 0 ? null : { ...questionSet, questions: kept };
    })
    .filter(Boolean);
}
